
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageCircle, RotateCcw, Heart, Repeat, Eye, CheckCircle2, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { StyledBadge } from '@/components/kol/StyledBadge';
import TweetKolTrackerDialog from './TweetKolTrackerDialog';
import { useToast } from '@/hooks/use-toast';
import { type TwitterSearchResult } from '@/types/twitterTypes';

interface KolTweetsViewProps {
  kolId: string;
  username: string;
}

// Define TweetData interface
interface TweetData {
  id: string;
  tweet_id: string;
  text: string;
  author_name: string;
  author_username: string; 
  created_at: string;
  profile_image_url: string;
  like_count: number;
  retweet_count: number;
  reply_count: number;
  impression_count: number;
  quote_count: number;
  author_id: string;
  raw_data?: any;
  deliverable_id: string; // Required from TwitterSearchResult
  deliverable_name?: string; // Added field for deliverable name
  in_kol_tracker?: boolean;
}

interface TrackedTweet {
  id: string;
  post_link: string;
  posting_date: string;
  expense: number;
  content_views: number;
  campaign_bu: string[]; // Changed from string to string[] to support jsonb array
  post_type: string;
  name: string;
  tweet_id?: string;
}

const KolTweetsView: React.FC<KolTweetsViewProps> = ({ kolId, username }) => {
  const [tweets, setTweets] = useState<TweetData[]>([]);
  const [trackedTweets, setTrackedTweets] = useState<TrackedTweet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedTweet, setSelectedTweet] = useState<TweetData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const [deliverableInfo, setDeliverableInfo] = useState<{id: string, name: string}[]>([]);
  
  const fetchDeliverableInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('twitter_deliverables')
        .select('id, name');
      
      if (error) throw error;
      
      if (data) {
        setDeliverableInfo(data);
      }
    } catch (error) {
      console.error("Error fetching deliverable info:", error);
    }
  };
  
  const fetchTweets = async () => {
    setIsLoading(true);
    try {
      // First fetch deliverable information
      await fetchDeliverableInfo();
      
      // Clean username (remove @ if present)
      const cleanUsername = username.startsWith('@') ? username.substring(1) : username;
      
      // Fetch tweets from twitter_search_results
      const { data: twitterData, error: twitterError } = await supabase
        .from('twitter_search_results')
        .select('*')
        .eq('author_username', cleanUsername)
        .order('created_at', { ascending: false })
        .limit(50);
        
      if (twitterError) throw twitterError;
      
      // Fetch tracked tweets from kol_tracker that correspond to this KOL
      const { data: trackedData, error: trackedError } = await supabase
        .from('kol_tracker')
        .select('*')
        .eq('kol_db_id', kolId)
        .eq('platform', 'Twitter')
        .order('posting_date', { ascending: false });
        
      if (trackedError) throw trackedError;
      
      console.log(`Fetched ${twitterData?.length || 0} Twitter results and ${trackedData?.length || 0} tracked tweets`);
      
      // Process tracked tweets to extract tweet IDs and handle campaign_bu as array
      const processedTrackedTweets = trackedData?.map(tweet => {
        // Extract tweet ID from post_link if available
        let tweetId = undefined;
        if (tweet.post_link) {
          const match = tweet.post_link.match(/\/status\/(\d+)/);
          if (match && match[1]) {
            tweetId = match[1];
          }
        }
        
        // Handle campaign_bu conversion from Json to string[]
        let campaignBu: string[] = [];
        if (tweet.campaign_bu) {
          if (Array.isArray(tweet.campaign_bu)) {
            campaignBu = tweet.campaign_bu.filter((item): item is string => typeof item === 'string');
          } else if (typeof tweet.campaign_bu === 'string') {
            campaignBu = [tweet.campaign_bu];
          }
        }
        
        return {
          ...tweet,
          tweet_id: tweetId,
          campaign_bu: campaignBu
        };
      }) || [];
      
      setTrackedTweets(processedTrackedTweets);
      
      // Enhance tweets with deliverable names and mark tweets that are already tracked
      const processedTweets = twitterData?.map(tweet => {
        const isTracked = processedTrackedTweets.some(
          trackedTweet => trackedTweet.tweet_id === tweet.tweet_id
        );
        
        // Find matching deliverable name
        const matchingDeliverable = deliverableInfo.find(
          d => d.id === tweet.deliverable_id
        );
        
        return {
          ...tweet,
          in_kol_tracker: isTracked,
          deliverable_name: matchingDeliverable ? matchingDeliverable.name : undefined
        };
      }) || [];
      
      setTweets(processedTweets);
    } catch (err) {
      console.error('Error fetching KOL tweets:', err);
      toast({
        title: 'Error',
        description: 'Failed to load tweets',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (kolId && username) {
      fetchTweets();
    }
  }, [kolId, username]);
  
  const handleAddToKolTracker = (tweet: TweetData) => {
    setSelectedTweet(tweet);
    setIsDialogOpen(true);
  };
  
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    // Refresh the data to update the in_kol_tracker status
    fetchTweets();
  };

  const renderTweet = (tweet: TweetData) => {
    const tweetDate = tweet.created_at ? new Date(tweet.created_at) : null;
    
    return (
      <Card 
        key={tweet.id} 
        className="flex flex-col h-full transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
      >
        <CardContent className="pt-4 pb-2 flex-grow relative">
          {/* Add to Tracker Button in the top right */}
          {!tweet.in_kol_tracker && (
            <Button 
              variant="outline" 
              size="icon"
              className="absolute top-2 right-2 h-7 w-7"
              onClick={() => handleAddToKolTracker(tweet)}
              title="Add to KOL Tracker"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}

          {/* Show badge if it's already in KOL Tracker */}
          {tweet.in_kol_tracker && (
            <div className="absolute top-2 right-2">
              <Badge variant="outline" className="flex items-center gap-1 bg-green-50 text-green-800 border-green-200">
                <CheckCircle2 className="h-3 w-3" />
                <span className="text-[10px]">In KOL Tracker</span>
              </Badge>
            </div>
          )}
          
          <div className="flex items-start gap-3 mb-3 mt-2">
            {tweet.profile_image_url && (
              <img 
                src={tweet.profile_image_url} 
                alt={tweet.author_name} 
                className="w-10 h-10 rounded-full"
              />
            )}
            <div>
              <div className="font-semibold">{tweet.author_name}</div>
              <div className="text-sm text-muted-foreground">@{tweet.author_username}</div>
            </div>
          </div>
          
          <p className="text-sm mb-3 line-clamp-4">{tweet.text}</p>
          
          {tweetDate && (
            <div className="text-xs text-muted-foreground mb-3">
              {format(tweetDate, 'MMM d, yyyy · h:mm a')}
            </div>
          )}
        </CardContent>
        
        <CardFooter className="border-t pt-3 flex justify-between">
          <div className="flex gap-4">
            <div className="flex items-center gap-1 text-xs">
              <MessageCircle className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{tweet.reply_count || 0}</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{tweet.retweet_count || 0}</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <Heart className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{tweet.like_count || 0}</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{tweet.impression_count || 0}</span>
            </div>
          </div>
          
          {/* Add Deliverable Badge if available */}
          {tweet.deliverable_name && (
            <Badge variant="primary" className="text-xs">
              {tweet.deliverable_name}
            </Badge>
          )}
        </CardFooter>
      </Card>
    );
  };
  
  // Filter tweets based on active tab
  const filteredTweets = activeTab === 'all' 
    ? tweets 
    : activeTab === 'tracked' 
      ? tweets.filter(tweet => tweet.in_kol_tracker)
      : tweets.filter(tweet => !tweet.in_kol_tracker);
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="mb-4">
            <TabsTrigger value="all">All Tweets</TabsTrigger>
            <TabsTrigger value="tracked">KOL Posting Links</TabsTrigger>
            <TabsTrigger value="untracked">Not Tracked</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <Button 
          variant="outline" 
          size="sm" 
          className="mb-4"
          onClick={fetchTweets}
          disabled={isLoading}
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="h-[250px]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-3">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-4/5" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTweets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTweets.map(renderTweet)}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No tweets found</p>
        </div>
      )}
      
      {/* Dialog for adding tweet to KOL Tracker */}
      {selectedTweet && (
        <TweetKolTrackerDialog
          tweet={selectedTweet as TwitterSearchResult}
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
};

export default KolTweetsView;
